import { FC, forwardRef, useMemo } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";

interface Props {
  bgcolor: string;
  displayText: string;
  fgcolor: string;
  percent: number;
  size: number;
  strokeWidth: number;
}

export const PercentIndicatorCircle = forwardRef<HTMLDivElement, Props>(
  (
    { bgcolor, displayText, fgcolor, percent, size, strokeWidth, ...props },
    ref
  ) => {
    const percentClamped = useMemo(() => {
      if (percent < 0) {
        return 0;
      } else if (percent > 100) {
        return 100;
      } else {
        return percent;
      }
    }, [percent]);
    const radius = useMemo(() => size / 2 - strokeWidth / 2, [
      size,
      strokeWidth
    ]);
    const circumference = useMemo(() => Math.PI * 2 * radius, [radius]);
    const pct = useMemo(() => circumference * (percentClamped / 100), [
      circumference,
      percentClamped
    ]);

    return (
      <Container ref={ref} {...props}>
        <Svg
          aria-hidden="true"
          width={size}
          height={size}
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          data-testid="percent-indicator"
        >
          <Circle
            color={bgcolor}
            r={size / 2 - strokeWidth / 2}
            cx={size / 2}
            cy={size / 2}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset="0"
            strokeWidth={strokeWidth}
            data-testid="background"
          />

          <Circle
            color={fgcolor}
            r={size / 2 - strokeWidth / 2}
            cx={size / 2}
            cy={size / 2}
            fill="transparent"
            strokeDasharray={`${pct} ${circumference - pct}`}
            strokeDashoffset="0"
            strokeWidth={strokeWidth}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            data-testid="foreground"
          />
        </Svg>

        <FitText text={displayText} size={size} data-testid="text" />
      </Container>
    );
  }
);

PercentIndicatorCircle.displayName = "PercentIndicatorCircle";

const Container = styled(Box)`
  display: grid;
  width: fit-content;

  > * {
    grid-area: 1 / 1;
    align-self: center;
    justify-self: center;
  }
`;

const Svg = styled("svg")`
  width: 100%;
  height: 100%;
`;

const Circle = styled("circle")`
  stroke-dashoffset: 0;
  transition: stroke-dashoffset 0.5s linear;
  stroke: ${props => props.color};
`;

export const FitText: FC<{ size: number; text: string }> = ({
  size,
  text
}): JSX.Element => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 45 10"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    data-testid="fit-text"
  >
    <text x="50%" y="65%" dominantBaseline="middle" textAnchor="middle">
      {text}
    </text>
  </svg>
);
